<script setup>
import { computed, defineProps, nextTick, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Icon from '../components/IconComponent.vue';
import { api } from '../services/api';

defineProps({
    navigate: {
        type: Function,
        required: true
    }
});

const route = useRoute();

const verificationState = ref('verifying'); // verifying | paid | pending | failed | error | missing
const verificationError = ref('');
const payment = ref(null);

const bookingEmbedTarget = ref(null);
const isEmbedMounted = ref(false);
const embedError = ref('');

const whatsappLink =
    'https://wa.me/2347080900495?text=Hello%2C%20I%20have%20completed%20my%20payment%20and%20need%20help%20scheduling%20my%20consultation.';

const reference = computed(() => route.query.reference || '');
const accessCode = computed(() => route.query.access_code || '');

const formattedAmount = computed(() => {
    if (!payment.value) return '—';
    const amount = (Number(payment.value.amount_kobo) || 0) / 100;
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: payment.value.currency || 'NGN',
        maximumFractionDigits: 0
    }).format(amount);
});

const displayDate = computed(() =>
    new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date())
);

// The API returns only structured booking data (namespace + cal link); it never
// returns HTML/JS. We load the official Cal.com embed script ourselves and only
// accept strictly-validated values, so nothing from the network can become code.
const CAL_EMBED_SCRIPT = 'https://app.cal.com/embed/embed.js';
const CAL_ELEMENT_ID = 'cal-booking-embed';
const SAFE_NAMESPACE = /^[A-Za-z0-9_-]+$/;
const SAFE_CAL_LINK = /^[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+$/;

const bookingConfig = computed(() => {
    const data = payment.value;
    if (!data) return null;
    const namespace = data.booking_namespace;
    const calLink = data.booking_cal_link;
    if (typeof namespace !== 'string' || !SAFE_NAMESPACE.test(namespace)) return null;
    if (typeof calLink !== 'string' || !SAFE_CAL_LINK.test(calLink)) return null;
    return { namespace, calLink };
});

// Mirrors the official Cal.com embed snippet's queue loader, but the script src
// is a fixed first-party constant instead of HTML supplied by the backend.
function ensureCalLoader() {
    if (window.Cal) return;
    const push = (target, args) => {
        target.q.push(args);
    };
    window.Cal = function () {
        const cal = window.Cal;
        const args = arguments;
        if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            document.head.appendChild(document.createElement('script')).src = CAL_EMBED_SCRIPT;
            cal.loaded = true;
        }
        if (args[0] === 'init') {
            const api = function () {
                push(api, arguments);
            };
            const namespace = args[1];
            api.q = api.q || [];
            if (typeof namespace === 'string') {
                cal.ns[namespace] = cal.ns[namespace] || api;
                push(cal.ns[namespace], args);
                push(cal, ['initNamespace', namespace]);
            } else {
                push(cal, args);
            }
            return;
        }
        push(cal, args);
    };
}

function mountBookingEmbed() {
    const config = bookingConfig.value;
    if (!config || !bookingEmbedTarget.value) return;
    try {
        ensureCalLoader();
        window.Cal('init', config.namespace, { origin: 'https://app.cal.com' });
        window.Cal.config = window.Cal.config || {};
        window.Cal.config.forwardQueryParams = true;
        window.Cal.ns[config.namespace]('inline', {
            elementOrSelector: `#${CAL_ELEMENT_ID}`,
            config: { layout: 'month_view', useSlotsViewOnSmallScreen: 'true' },
            calLink: config.calLink
        });
        window.Cal.ns[config.namespace]('ui', {
            hideEventTypeDetails: false,
            layout: 'month_view'
        });
        isEmbedMounted.value = true;
    } catch (error) {
        embedError.value = error && error.message ? error.message : String(error);
    }
}

async function verify() {
    if (!reference.value) {
        verificationState.value = 'missing';
        return;
    }
    verificationState.value = 'verifying';
    verificationError.value = '';
    embedError.value = '';
    try {
        const data = await api.verifyPayment(reference.value, accessCode.value);
        payment.value = data;
        if (data.status === 'paid') {
            verificationState.value = 'paid';
            await nextTick();
            mountBookingEmbed();
        } else if (data.status === 'pending') {
            verificationState.value = 'pending';
        } else {
            verificationState.value = 'failed';
        }
    } catch (error) {
        verificationError.value = error.message || 'We could not verify this payment.';
        verificationState.value = 'error';
    }
}

onMounted(verify);
</script>

<template>
    <main class="relative overflow-hidden bg-gray-50 py-16 md:py-24">
        <div class="absolute -top-24 -left-24 w-72 h-72 bg-emerald-200/40 rounded-full blur-3xl pointer-events-none"></div>
        <div class="absolute -bottom-24 -right-24 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>

        <div class="container mx-auto px-4 relative z-10">
            <!-- Verifying -->
            <div v-if="verificationState === 'verifying'" class="max-w-xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 p-10 text-center">
                <div class="w-12 h-12 mx-auto mb-5 rounded-full border-4 border-primary/20 border-t-primary animate-spin"></div>
                <h1 class="text-2xl font-bold text-gray-800">Verifying your payment…</h1>
                <p class="text-gray-500 mt-2">Please hold on while we confirm your transaction with Paystack.</p>
            </div>

            <!-- Error / failed / missing -->
            <div v-else-if="verificationState === 'error' || verificationState === 'failed' || verificationState === 'missing'"
                 class="max-w-xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div class="h-1.5 bg-gradient-to-r from-primary via-rose-500 to-primary"></div>
                <div class="p-8 md:p-10 text-center">
                    <span class="w-16 h-16 mx-auto mb-5 rounded-full bg-rose-50 border border-rose-100 text-primary flex items-center justify-center">
                        <Icon name="x-circle" class-name="w-8 h-8" />
                    </span>
                    <h1 class="text-2xl font-bold text-gray-900">We couldn't confirm this payment</h1>
                    <p class="text-gray-600 mt-3">
                        {{ verificationState === 'missing'
                            ? 'No payment reference was provided.'
                            : (verificationError || 'The transaction was not completed. You have not been charged for a failed payment.') }}
                    </p>

                    <div v-if="reference" class="mt-6 bg-gray-50 rounded-xl p-4 border border-gray-100 text-left">
                        <p class="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Reference</p>
                        <p class="font-mono font-semibold text-gray-800 mt-1 break-all">{{ reference }}</p>
                    </div>

                    <div class="mt-8 flex flex-col sm:flex-row gap-3">
                        <button @click="navigate('scheduling')"
                                class="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-primary/20 hover:bg-primary/90 transition duration-300">
                            <Icon name="refresh" class-name="w-5 h-5" />
                            Try Again
                        </button>
                        <a :href="whatsappLink" target="_blank" rel="noopener"
                           class="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-3.5 px-6 rounded-xl shadow-lg hover:bg-[#20ba5a] transition duration-300">
                            <Icon name="whatsapp" class-name="w-5 h-5" />
                            Contact Support
                        </a>
                    </div>
                </div>
            </div>

            <!-- Pending -->
            <div v-else-if="verificationState === 'pending'" class="max-w-xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 p-10 text-center">
                <span class="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-50 border border-amber-100 text-amber-500 flex items-center justify-center">
                    <Icon name="time" class-name="w-8 h-8" />
                </span>
                <h1 class="text-2xl font-bold text-gray-900">Payment is still processing</h1>
                <p class="text-gray-600 mt-3">We haven't received the final confirmation from Paystack yet. This usually resolves within a minute.</p>
                <div class="mt-6 bg-gray-50 rounded-xl p-4 border border-gray-100 text-left">
                    <p class="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Reference</p>
                    <p class="font-mono font-semibold text-gray-800 mt-1 break-all">{{ reference }}</p>
                </div>
                <button @click="verify"
                        class="mt-8 inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3.5 px-8 rounded-xl shadow-lg shadow-primary/20 hover:bg-primary/90 transition duration-300">
                    <Icon name="refresh" class-name="w-5 h-5" />
                    Check Again
                </button>
            </div>

            <!-- Paid: success + Cal.com scheduler -->
            <div v-else class="max-w-3xl mx-auto space-y-8">
                <div v-reveal:up class="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                    <div class="h-1.5 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-400"></div>
                    <div class="p-8 md:p-10 text-center">
                        <span class="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30">
                            <Icon name="check" class-name="w-8 h-8" />
                        </span>
                        <span class="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
                            <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                            Payment Confirmed
                        </span>
                        <h1 class="text-3xl md:text-4xl font-extrabold text-gray-900">Transaction Successful</h1>
                        <p class="text-gray-600 max-w-xl mx-auto mt-3">Your payment was completed successfully.</p>

                        <dl class="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 text-left">
                            <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                <dt class="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Reference</dt>
                                <dd class="font-mono font-semibold text-gray-800 mt-1 break-all">{{ payment.reference }}</dd>
                            </div>
                            <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                <dt class="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Amount</dt>
                                <dd class="font-semibold text-gray-800 mt-1">{{ formattedAmount }}</dd>
                            </div>
                            <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                <dt class="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Date</dt>
                                <dd class="font-semibold text-gray-800 mt-1">{{ displayDate }}</dd>
                            </div>
                        </dl>
                    </div>
                </div>

                <div v-reveal:up class="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                    <div class="p-8 md:p-10">
                        <div class="flex items-start gap-4 mb-6">
                            <div class="bg-primary/10 p-2.5 rounded-xl flex-shrink-0">
                                <Icon name="calendar" class-name="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <h2 class="text-2xl font-bold text-gray-800">Pick your consultation time</h2>
                            </div>
                        </div>

                        <template v-if="bookingConfig">
                            <div v-if="!isEmbedMounted" class="text-gray-500 text-sm">Loading scheduler…</div>
                            <div class="rounded-2xl border border-gray-100 overflow-hidden min-h-[620px]">
                                <div :id="CAL_ELEMENT_ID" ref="bookingEmbedTarget" style="width:100%;height:100%;overflow:scroll"></div>
                            </div>
                            <p v-if="embedError" class="mt-4 text-sm text-primary flex items-start gap-2">
                                <Icon name="alert-circle" class-name="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>{{ embedError }} — please contact us on WhatsApp and we'll schedule you manually.</span>
                            </p>
                        </template>
                        <p v-else class="text-gray-600 text-sm">
                            Your payment is confirmed. To schedule this consultation, message us on
                            <a :href="whatsappLink" target="_blank" rel="noopener" class="text-primary font-semibold hover:underline">WhatsApp</a>.
                        </p>
                    </div>
                </div>

                <div class="flex flex-col sm:flex-row gap-3">
                    <button @click="navigate('scheduling')"
                            class="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 font-bold py-3.5 px-6 rounded-xl hover:border-primary hover:text-primary transition duration-300">
                        <Icon name="credit-card" class-name="w-5 h-5" />
                        Book Another Consultation
                    </button>
                    <button @click="navigate('home')"
                            class="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 font-bold py-3.5 px-6 rounded-xl hover:border-primary hover:text-primary transition duration-300">
                        <Icon name="home" class-name="w-5 h-5" />
                        Back to Home
                    </button>
                </div>

                <p class="text-xs text-gray-500 flex items-center justify-center gap-1.5">
                    <Icon name="lock-closed" class-name="w-4 h-4 text-gray-400" />
                    Secured transaction. Keep your reference number for your records.
                </p>
            </div>
        </div>
    </main>
</template>

<style scoped>
.animate-spin {
    animation: spin 0.9s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
    .animate-spin {
        animation-duration: 2.5s;
    }
}
</style>
